import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { AuthContext } from './AuthContext';

const CACHE_KEY_PREFIX = 'ws_';
const CACHE_TTL = 5 * 60 * 1000;

function getCachedProfile(uid) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${uid}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) {
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${uid}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedProfile(uid, data) {
  try {
    sessionStorage.setItem(
      `${CACHE_KEY_PREFIX}${uid}`,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    // Ignore write failures (e.g. sessionStorage full or unavailable in private mode)
  }
}

function clearCachedProfile(uid) {
  try {
    sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${uid}`);
  } catch {
    // Ignore removal failures (e.g. sessionStorage unavailable in private mode)
  }
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastFetchedUid = useRef(null);
  const profileAbortRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingSessionRef = useRef(null);
  const handlingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const processSession = async (currentSession) => {
      if (!mountedRef.current) return;
      setSession(currentSession);

      if (!currentSession?.user) {
        lastFetchedUid.current = null;
        if (profileAbortRef.current) profileAbortRef.current.abort();
        if (mountedRef.current) {
          setCurrentUser(null);
          setLoading(false);
        }
        return;
      }

      if (currentSession.user.id !== lastFetchedUid.current) {
        lastFetchedUid.current = currentSession.user.id;
        if (profileAbortRef.current) profileAbortRef.current.abort();
        const controller = new AbortController();
        profileAbortRef.current = controller;
 await fetchUserProfile(currentSession.access_token, currentSession.user.id, controller.signal);      }
    };

    const handleSession = async (currentSession) => {
      if (handlingRef.current) {
        // Queue the latest session — don't drop it
        pendingSessionRef.current = currentSession;
        return;
      }
      handlingRef.current = true;
      try {
        await processSession(currentSession);
        // Drain any queued session that arrived while we were processing
        while (pendingSessionRef.current !== null) {
          const next = pendingSessionRef.current;
          pendingSessionRef.current = null;
          await processSession(next);
        }
      } finally {
        handlingRef.current = false;
      }
    };

    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (window.location.hash.includes('type=recovery')) {
        if (mountedRef.current) setLoading(false);
        return;
      }
      handleSession(s);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, s) => {
      if (window.location.pathname === '/reset-password') return;
      await handleSession(s);
    });

    return () => {
      subscription.unsubscribe();
      if (profileAbortRef.current) profileAbortRef.current.abort();
    };
  }, []);

 const fetchUserProfile = async (accessToken, uid, signal) => {
    // 1. Serve from cache if fresh
    const cached = getCachedProfile(uid);
    if (cached) {
      if (mountedRef.current && !signal.aborted) {
        const frontendRole = cached.role === 'employee' ? 'user' : cached.role;
        setCurrentUser({ ...cached, role: frontendRole });
        setLoading(false);
      }
      return;
    }

    try {
      const res = await fetch(
        `${import.meta.env.VITE_REACT_APP_API_URL}/api/user/web-session`,
        { headers: { Authorization: `Bearer ${accessToken}` }, signal }
      );

      if (!res.ok) {
        if (res.status === 429) {
          console.warn('[AuthProvider] Rate limited on web-session, will retry on next auth state change');
          lastFetchedUid.current = null; // allow the next auth state change to retry (uid guard would otherwise block it)
          if (mountedRef.current && !signal.aborted) setLoading(false);
          return;
        }
        const err = new Error(`web-session failed: ${res.status}`);
        err.status = res.status;
        throw err;
      }

      const { data } = await res.json();

      if (!signal.aborted) {
        // 2. Persist to sessionStorage before setting state
        setCachedProfile(uid, data);
      }

      if (mountedRef.current && !signal.aborted) {
        const frontendRole = data.role === 'employee' ? 'user' : data.role;
        setCurrentUser({ ...data, role: frontendRole });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[AuthProvider] Error fetching web-session:', err);
      lastFetchedUid.current = null; // allow retry; the uid guard would otherwise block recovery
      if (mountedRef.current) {
        setError(err);
        setCurrentUser(null);
      }
    } finally {
      if (!signal.aborted && mountedRef.current) setLoading(false);
    }
  };

  // Call this after any profile update (name, avatar, role change, etc.)
  const invalidateProfileCache = useCallback(() => {
    if (session?.user?.id) {
      clearCachedProfile(session.user.id);
    }
  }, [session?.user?.id]);

  // Synchronously clear the displayed user before starting a new OAuth flow.
  // signOut()'s SIGNED_OUT event is async and can leave the previous user
  // rendered in the UI for the duration of the redirect to the provider.
  const clearSession = useCallback(() => {
    if (session?.user?.id) clearCachedProfile(session.user.id);
    lastFetchedUid.current = null;
    if (profileAbortRef.current) profileAbortRef.current.abort();
    setCurrentUser(null);
    setSession(null);
    setLoading(true);
  }, [session?.user?.id]);

  // Invalidate the cached profile and refetch it from the backend, then update state.
  // Used after a profile edit so the UI reflects new data without a full page reload.
  const refreshProfile = useCallback(async () => {
    if (!session?.user?.id) return;
    clearCachedProfile(session.user.id);
    if (profileAbortRef.current) profileAbortRef.current.abort();
    const controller = new AbortController();
    profileAbortRef.current = controller;
    await fetchUserProfile(session.access_token, session.user.id, controller.signal);
  }, [session?.user?.id, session?.access_token]);

  const value = { currentUser, session, loading, error, setError, invalidateProfileCache, refreshProfile, clearSession };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};